import { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  extractSelectedEntitiesFromFilter,
  getProjectsToAddToPmImportStrategyByBoardsIds
} from 'common/services/pm.service';
import useSelectedEntities from './PMHooks/useSelectedEntities';
import usePmImportStrategyEntity from './PMHooks/usePmImportStrategyEntity';
import { useSelector } from 'react-redux';
import { boardsProviderKeysSelector } from 'state/selectors/boards';
import useSettingsService from 'components/hooks/useSettingsService';
import { SettingValues } from '@linearb/linjs-common';
import { getTooManyEntitiesSelectedErrorMessage } from '../../../../../services/project-management.service';
import useEntitiesLimitation from 'views/root/views/home/hooks/useEntitiesLimitation';
import { DeviationError } from 'common/constants/error-types';

const isProjectSelectedInOneOfTeamsSetting = (
  pmToolConfig,
  projectProviderId,
  projectsFromDB,
  entityName
) =>
  projectsFromDB.filter(({ filter }) =>
    extractSelectedEntitiesFromFilter[pmToolConfig.sensor_id](
      filter,
      entityName
    ).find(
      ({ provider_id }) => String(provider_id) === String(projectProviderId)
    )
  );

const isProjectSelectedInPDT = (boards, providerId) =>
  boards
    ? _.get(
        _.find(boards, ({ provider_id }) => provider_id === providerId),
        'references.projects',
        []
      )
    : [];

const appendUsedByToEntities = ({
  boards,
  pmToolConfig,
  providerEntities,
  projectsFromDB,
  entityName,
  pmImportSettings
}) => {
  return providerEntities.map(project => {
    let hasBoardsInUsed = false;
    // here we are listing all boards associated with this project and check if one of them selected in pm import strategy.
    // in this case - we need to disable the option to un-select the project
    if (entityName === pmToolConfig.entity_names.project) {
      const { boards: boardsFromPmSettings = [] } = pmImportSettings;
      const relevantBoards = boards.filter(
        ({ provider_project_id }) => provider_project_id === project.provider_id
      );
      const boardsProviderIds = boardsFromPmSettings.map(
        ({ provider_id }) => provider_id
      );
      hasBoardsInUsed = relevantBoards
        .map(({ provider_id }) => provider_id)
        .some(board => boardsProviderIds.includes(board));
    }
    const teamsWhoSelectedThisProj = isProjectSelectedInOneOfTeamsSetting(
      pmToolConfig,
      project.provider_id,
      projectsFromDB,
      entityName
    );
    const PDTWhoSelectedThisProj = isProjectSelectedInPDT(
      boards, // Shortcut PDT not supported
      project.provider_id
    );

    return {
      ...project,
      usedByTeams: teamsWhoSelectedThisProj.map(({ name }) => name),
      usedByProjects: PDTWhoSelectedThisProj.map(
        ({ project_name }) => project_name
      ),
      hasBoardsInUsed
    };
  });
};

export const usePMEntities = ({
  boards = [],
  projects: projectsFromDB = [],
  providerEntities,
  providerEntitiesWithExtras,
  pmToolConfig,
  entityName
}) => {
  const { getSettingOrDefinitionValue } = useSettingsService();
  const pmImportSettings = getSettingOrDefinitionValue({
    name: SettingValues.PM_IMPORT_STRATEGY
  });

  const { entitiesDeviation, entitiesLimit } =
    useEntitiesLimitation(entityName);
  const [entities, setEntities] = useState([]);
  const selectedEntitiesSetting = usePmImportStrategyEntity({ entityName });
  const selectedProjectsSetting = usePmImportStrategyEntity({
    entityName: pmToolConfig.entity_names.project
  });
  const allBoardsByProviderIds = useSelector(boardsProviderKeysSelector);
  const selectedEntities = useSelectedEntities({
    defaultEntities: selectedEntitiesSetting.values,
    entityKey: 'provider_id'
  });
  const isSaveEnabled =
    _.xorBy(
      selectedEntities.values,
      selectedEntitiesSetting.values,
      'provider_id'
    ).length > 0;

  // this function go over all selected boards and selects the projects those boards associated with
  const syncSelectedBoardsProjects = () => {
    const boardsIds = selectedEntities.values.map(
      ({ provider_id }) => provider_id
    );
    const relevantProjects = getProjectsToAddToPmImportStrategyByBoardsIds(
      boardsIds,
      selectedProjectsSetting.values,
      allBoardsByProviderIds
    );
    return relevantProjects;
  };

  const onSaveChangesClick = async () => {
    let relevantProjects;
    if (entityName === pmToolConfig.entity_names.board) {
      relevantProjects = syncSelectedBoardsProjects();
    }

    const deviation = entitiesDeviation(selectedEntities.values);

    const errorMessage = getTooManyEntitiesSelectedErrorMessage({
      entityName,
      limit: entitiesLimit,
      deviation
    });
    if (deviation > 0) {
      throw new DeviationError(errorMessage);
    }

    selectedEntitiesSetting.onSave(selectedEntities.values, relevantProjects);
  };

  const getMergedEntitiesWithExtra = entities => {
    const entitiesWithExtraKeys = _.keyBy(
      providerEntitiesWithExtras,
      'provider_id'
    );
    const mergedEntities = entities.map(entity => {
      const entityExtra = entitiesWithExtraKeys[entity.provider_id] || {};
      return {
        ...entity,
        ...entityExtra
      };
    });
    return mergedEntities;
  };

  const initEntities = () => {
    let entities = appendUsedByToEntities({
      boards,
      pmToolConfig,
      providerEntities,
      projectsFromDB,
      entityName,
      pmImportSettings
    });
    if (providerEntitiesWithExtras.length > 0)
      entities = getMergedEntitiesWithExtra(entities);
    setEntities(entities);
  };

  useEffect(() => {
    initEntities();
  }, [providerEntities.length, providerEntitiesWithExtras.length]);

  return {
    entities,
    selectedEntities,
    isSaveEnabled,
    onSaveChangesClick
  };
};
